/**
 * Chunk Error Recovery Handler v2.0.0
 * 
 * Handles ChunkLoadError, "Loading chunk failed", and dynamic import failures.
 * Implements a safe, single-retry recovery flow to prevent infinite loops.
 * 
 * CRITICAL: This module NEVER touches localStorage, sessionStorage, or IndexedDB.
 * Only Workbox-managed caches are cleared. Auth tokens are preserved.
 */

import { logger } from '@/lib/logger';

// Session flags to prevent infinite loops
const CHUNK_RECOVERY_ATTEMPTED_KEY = 'llx:chunk_recovery_attempted';
const SW_FAILURE_COUNT_KEY = 'llx:sw_failure_count';
const SW_FAILURE_WINDOW_KEY = 'llx:sw_failure_window_start';

// Safety valve thresholds
const MAX_SW_FAILURES = 3;
const SW_FAILURE_WINDOW_MS = 30000; // 30 seconds

// Cache names that are safe to delete (Workbox runtime caches for chunks)
const SAFE_TO_DELETE_CACHE_PATTERNS = [
  'js-chunks',
  'css-chunks',
  'lazy-chunks',
  'static-assets',
  'navigation-cache',
];

// Caches we must NEVER delete (fonts, precache manifest)
const PROTECTED_CACHE_PATTERNS = [
  'workbox-precache',
  'google-fonts',
];

/**
 * Check if a cache name is safe to delete
 */
const isSafeToDeleteCache = (cacheName: string): boolean => {
  // Never delete protected caches
  if (PROTECTED_CACHE_PATTERNS.some(p => cacheName.includes(p))) {
    return false;
  }
  // Only delete caches matching our safe patterns
  return SAFE_TO_DELETE_CACHE_PATTERNS.some(p => cacheName.includes(p));
};

/**
 * Check if chunk error recovery was already attempted this session
 */
export const wasRecoveryAttempted = (): boolean => {
  return sessionStorage.getItem(CHUNK_RECOVERY_ATTEMPTED_KEY) === 'true';
};

/**
 * Mark that recovery was attempted
 */
const markRecoveryAttempted = () => {
  sessionStorage.setItem(CHUNK_RECOVERY_ATTEMPTED_KEY, 'true');
};

/**
 * Clear recovery flag (call when app loads successfully)
 */
export const clearRecoveryFlag = () => {
  sessionStorage.removeItem(CHUNK_RECOVERY_ATTEMPTED_KEY);
  sessionStorage.removeItem(SW_FAILURE_COUNT_KEY);
  sessionStorage.removeItem(SW_FAILURE_WINDOW_KEY);
};

/**
 * Check if error is a chunk load failure
 */
export const isChunkLoadError = (error: Error | unknown): boolean => {
  if (!(error instanceof Error)) return false;
  
  const message = error.message.toLowerCase();
  const name = error.name?.toLowerCase() || '';
  
  return (
    name === 'chunkloaderror' ||
    message.includes('loading chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading css chunk') ||
    message.includes('error loading dynamically imported module') ||
    (message.includes('failed to load') && message.includes('module'))
  );
};

/**
 * Track SW fetch failures for safety valve
 */
const trackSwFailure = (): { shouldTriggerSafetyValve: boolean } => {
  const now = Date.now();
  const windowStart = parseInt(sessionStorage.getItem(SW_FAILURE_WINDOW_KEY) || '0', 10);
  
  // Reset window if expired
  if (now - windowStart > SW_FAILURE_WINDOW_MS) {
    sessionStorage.setItem(SW_FAILURE_WINDOW_KEY, now.toString());
    sessionStorage.setItem(SW_FAILURE_COUNT_KEY, '1');
    return { shouldTriggerSafetyValve: false };
  }
  
  // Increment failure count
  const count = parseInt(sessionStorage.getItem(SW_FAILURE_COUNT_KEY) || '0', 10) + 1;
  sessionStorage.setItem(SW_FAILURE_COUNT_KEY, count.toString());
  
  return { shouldTriggerSafetyValve: count >= MAX_SW_FAILURES };
};

/**
 * Wait for auth session to be confirmed before reloading
 * Returns true if session exists, false if no session or timeout
 */
const waitForAuthBeforeReload = async (timeoutMs = 3000): Promise<boolean> => {
  // Check if we have Supabase auth tokens in localStorage
  const hasAuthTokens = () => {
    try {
      const keys = Object.keys(localStorage);
      return keys.some(k => k.startsWith('sb-') && k.includes('auth-token'));
    } catch {
      return false;
    }
  };

  // If no auth tokens, proceed immediately
  if (!hasAuthTokens()) {
    logger.log('[ChunkRecovery] No auth tokens found, proceeding with reload');
    return false;
  }

  // Wait a short time for any pending token refresh
  await new Promise(resolve => setTimeout(resolve, 500));
  
  // Verify tokens still exist
  const stillHasTokens = hasAuthTokens();
  logger.log('[ChunkRecovery] Auth tokens present after wait:', stillHasTokens);
  
  return stillHasTokens;
};

/**
 * Perform safe chunk error recovery
 * Returns true if recovery was initiated (reload will happen)
 * 
 * CRITICAL: This function ONLY clears Workbox runtime caches.
 * It NEVER touches localStorage, sessionStorage (except recovery flags), or IndexedDB.
 */
export const recoverFromChunkError = async (): Promise<boolean> => {
  // Prevent infinite loops
  if (wasRecoveryAttempted()) {
    logger.log('[ChunkRecovery] Recovery already attempted this session, skipping');
    return false;
  }
  
  markRecoveryAttempted();
  
  // Dev instrumentation
  const hasAuthPreReload = Object.keys(localStorage).some(k => k.startsWith('sb-'));
  logger.log('[ChunkRecovery] Starting recovery flow...', { hasAuthPreReload });
  
  try {
    // Step 1: Try to update service worker
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.getRegistration();
      
      if (registration) {
        logger.log('[ChunkRecovery] Updating service worker...');
        await registration.update();
        
        // If there's a waiting worker, activate it
        if (registration.waiting) {
          logger.log('[ChunkRecovery] Activating waiting service worker...');
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
      }
    }
    
    // Step 2: Clear ONLY chunk-related caches (never auth storage!)
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      const cachesToDelete = cacheNames.filter(isSafeToDeleteCache);
      
      logger.log(`[ChunkRecovery] Clearing ${cachesToDelete.length} chunk caches:`, cachesToDelete);
      await Promise.all(cachesToDelete.map(name => caches.delete(name)));
    }
    
    // Step 3: Wait for auth before reload
    await waitForAuthBeforeReload();
    
    // Step 4: Reload the page
    logger.log('[ChunkRecovery] Reloading page...');
    window.location.reload();
    return true;
  } catch (err) {
    logger.log('[ChunkRecovery] Recovery failed:', err);
    
    // Force reload as last resort
    window.location.reload();
    return true;
  }
};

/**
 * SW Safety Valve - triggered on repeated SW fetch failures
 * Unregisters SW and clears runtime caches (NOT auth storage!)
 */
export const triggerSwSafetyValve = async (): Promise<boolean> => {
  const { shouldTriggerSafetyValve } = trackSwFailure();
  
  if (!shouldTriggerSafetyValve) {
    return false;
  }
  
  logger.log('[SW Safety Valve] Triggering due to repeated failures...');
  
  try {
    // Unregister all service workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map(r => r.unregister()));
    }
    
    // Clear ONLY runtime caches - NEVER touch storage!
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      // Be more aggressive here but still preserve fonts
      const cachesToDelete = cacheNames.filter(name => 
        !name.includes('google-fonts')
      );
      logger.log('[SW Safety Valve] Clearing caches:', cachesToDelete);
      await Promise.all(cachesToDelete.map(name => caches.delete(name)));
    }
    
    // Wait for auth before reload
    await waitForAuthBeforeReload();
    
    // Reload once
    window.location.reload();
    return true;
  } catch (err) {
    logger.log('[SW Safety Valve] Failed:', err);
    window.location.reload();
    return true;
  }
};

/**
 * Install global error handlers for chunk load failures
 * Call this once at app startup
 */
export const installChunkErrorHandlers = () => {
  // Clear recovery flag on successful load
  clearRecoveryFlag();
  
  // Dev instrumentation: log session restoration time
  if (import.meta.env.DEV) {
    const startTime = performance.now();
    const checkSession = () => {
      const hasSession = Object.keys(localStorage).some(k => k.startsWith('sb-'));
      if (hasSession) {
        const elapsed = performance.now() - startTime;
        logger.log(`[ChunkRecovery] Session detected in ${elapsed.toFixed(0)}ms`);
      } else {
        setTimeout(checkSession, 100);
      }
    };
    setTimeout(checkSession, 100);
  }
  
  // Handle unhandled promise rejections (dynamic imports)
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) {
      event.preventDefault();
      logger.log('[ChunkRecovery] Caught unhandled chunk error:', event.reason?.message);
      recoverFromChunkError();
    }
  });
  
  // Handle regular errors
  window.addEventListener('error', (event) => {
    if (event.error && isChunkLoadError(event.error)) {
      event.preventDefault();
      logger.log('[ChunkRecovery] Caught chunk load error:', event.error?.message);
      recoverFromChunkError();
    }
  });
};
